import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { LegalPage } from '@components';
import { email, siteUrl } from '@config';

/**
 * ВНИМАНИЕ: настоящий текст является шаблоном и перед публикацией
 * должен быть проверен специалистом по законодательству РФ
 * о персональных данных (в т.ч. Федеральный закон № 152-ФЗ).
 * При необходимости добавьте подтверждённые реквизиты оператора.
 */
const PrivacyPage = ({ location }) => (
  <LegalPage
    location={location}
    title="Политика конфиденциальности"
    updated="14 мая 2026 г."
    description="Политика конфиденциальности сайта SMART-KVADRAT: порядок обработки и защиты персональных данных, права субъектов персональных данных.">
    <h2>1. Общие положения</h2>
    <p>
      Настоящая Политика конфиденциальности (далее — Политика) определяет порядок обработки и
      защиты персональных данных пользователей сайта <strong>{siteUrl}</strong> (далее — Сайт) и
      действует в отношении всей информации, которую оператор может получить о пользователе во
      время использования Сайта.
    </p>
    <p>
      Политика разработана в соответствии с Конституцией Российской Федерации, Федеральным законом
      от 27.07.2006 № 152-ФЗ «О персональных данных» и иными нормативными правовыми актами
      Российской Федерации в области персональных данных.
    </p>
    <p>
      Используя Сайт, пользователь подтверждает, что ознакомлен с настоящей Политикой. Политика
      применяется только к данному Сайту и не распространяется на сторонние ресурсы, ссылки на
      которые могут присутствовать на Сайте.
    </p>

    <h2>2. Основные понятия</h2>
    <ul>
      <li>
        <strong>Персональные данные</strong> — любая информация, относящаяся к прямо или косвенно
        определённому или определяемому физическому лицу (субъекту персональных данных).
      </li>
      <li>
        <strong>Обработка персональных данных</strong> — любое действие (операция) или совокупность
        действий с персональными данными, совершаемых с использованием средств автоматизации или
        без использования таких средств.
      </li>
      <li>
        <strong>Оператор</strong> — лицо, самостоятельно или совместно с другими лицами
        организующее и (или) осуществляющее обработку персональных данных, а также определяющее
        цели и содержание обработки.
      </li>
      <li>
        <strong>Пользователь</strong> — посетитель Сайта.
      </li>
    </ul>

    <h2>3. Оператор персональных данных</h2>
    <p>
      Оператором персональных данных является индивидуальный предприниматель Пушкарева Мария
      Викторовна.
    </p>
    <ul>
      <li>Адрес электронной почты: {email}</li>
    </ul>

    <h2>4. Категории обрабатываемых данных</h2>
    <p>
      Сайт не содержит форм регистрации, личного кабинета, форм подписки и форм обратной связи с
      полями для ввода персональных данных. Оператор не собирает через Сайт специальные категории
      персональных данных и биометрические персональные данные.
    </p>
    <p>При использовании Сайта могут обрабатываться следующие данные:</p>
    <ul>
      <li>
        данные, автоматически передаваемые устройством пользователя при посещении Сайта, включая
        IP-адрес, данные о браузере и устройстве, дату и время обращения, адрес запрошенной
        страницы (в составе технических журналов сервера);
      </li>
      <li>
        данные, которые пользователь самостоятельно сообщает Оператору при обращении по
        электронной почте (имя, адрес электронной почты, содержание обращения и иные сведения,
        указанные пользователем по своей инициативе).
      </li>
    </ul>
    <p>
      Оператор исходит из того, что пользователь предоставляет достоверные данные и не сообщает
      избыточную информацию.
    </p>

    <h2>5. Цели обработки персональных данных</h2>
    <ul>
      <li>обеспечение функционирования и безопасности Сайта;</li>
      <li>обработка обращений пользователей и предоставление ответов;</li>
      <li>заключение и исполнение договоров на оказание услуг (при обращении пользователя);</li>
      <li>выполнение требований законодательства Российской Федерации.</li>
    </ul>

    <h2>6. Правовые основания обработки</h2>
    <ul>
      <li>Федеральный закон от 27.07.2006 № 152-ФЗ «О персональных данных»;</li>
      <li>согласие субъекта персональных данных на обработку его персональных данных;</li>
      <li>исполнение договора, стороной которого является субъект персональных данных.</li>
    </ul>

    <h2>7. Файлы cookie и локальное хранилище</h2>
    <p>
      Сайт не использует файлы cookie для сбора аналитики, показа рекламы или отслеживания
      поведения пользователей. На Сайте не установлены системы веб-аналитики, рекламные пиксели,
      счётчики и иные сторонние сервисы отслеживания.
    </p>
    <p>
      Для обеспечения работы Сайта в режиме офлайн может использоваться механизм кэширования
      браузера (service worker) и локальное хранилище браузера. Эти данные не содержат информации,
      позволяющей идентифицировать пользователя, и не передаются Оператору.
    </p>

    <h2>8. Порядок и условия обработки</h2>
    <p>
      Обработка персональных данных осуществляется с использованием средств автоматизации и без
      использования таких средств, на принципах законности, ограничения обработки заранее
      заявленными целями, соответствия содержания и объёма обрабатываемых данных целям обработки.
    </p>
    <p>
      Оператор не принимает решений, порождающих юридические последствия для субъекта
      персональных данных, исключительно на основании автоматизированной обработки.
    </p>

    <h2>9. Передача данных третьим лицам и трансграничная передача</h2>
    <p>
      Оператор может привлекать для обеспечения работы Сайта хостинг-провайдера
      ([наименование хостинг-провайдера]), действующего на основании договора и обеспечивающего
      хранение данных на серверах, расположенных на территории Российской Федерации.
    </p>
    <p>
      Оператор не осуществляет трансграничную передачу персональных данных. Передача персональных
      данных третьим лицам не осуществляется, за исключением случаев, прямо предусмотренных
      законодательством Российской Федерации.
    </p>

    <h2>10. Сроки хранения персональных данных</h2>
    <p>
      Персональные данные хранятся не дольше, чем этого требуют цели их обработки. Технические
      журналы сервера хранятся в течение срока, необходимого для обеспечения безопасности и
      работоспособности Сайта. Данные обращений хранятся до достижения целей обработки или до
      отзыва согласия субъектом персональных данных, если иной срок не установлен
      законодательством.
    </p>

    <h2>11. Меры по защите персональных данных</h2>
    <p>
      Оператор принимает необходимые правовые, организационные и технические меры для защиты
      персональных данных от неправомерного или случайного доступа, уничтожения, изменения,
      блокирования, копирования, предоставления и распространения, в том числе:
    </p>
    <ul>
      <li>ограничение доступа к персональным данным;</li>
      <li>применение защищённых протоколов передачи данных (HTTPS);</li>
      <li>учёт требований информационной безопасности при проектировании решений;</li>
      <li>контроль за принимаемыми мерами защиты.</li>
    </ul>

    <h2>12. Права субъекта персональных данных</h2>
    <p>Субъект персональных данных имеет право:</p>
    <ul>
      <li>получать информацию, касающуюся обработки его персональных данных;</li>
      <li>требовать уточнения, блокирования или уничтожения персональных данных;</li>
      <li>отозвать согласие на обработку персональных данных;</li>
      <li>обжаловать действия Оператора в уполномоченный орган по защите прав субъектов
        персональных данных (Роскомнадзор) или в судебном порядке.</li>
    </ul>

    <h2>13. Порядок обращения и отзыва согласия</h2>
    <p>
      Обращения, связанные с обработкой персональных данных, направляются на адрес электронной
      почты <strong>{email}</strong>. Оператор рассматривает обращения в срок,
      установленный законодательством Российской Федерации.
    </p>
    <p>
      Согласие на обработку персональных данных может быть отозвано путём направления
      соответствующего письменного заявления на указанный адрес электронной почты. После отзыва
      согласия обработка прекращается, а персональные данные уничтожаются, если отсутствуют иные
      законные основания для их обработки.
    </p>
    <p>
      Порядок обработки персональных данных подробно описан в разделе{' '}
      <Link to="/personal-data">«Обработка персональных данных»</Link>.
    </p>

    <h2>14. Изменение Политики</h2>
    <p>
      Оператор вправе изменять настоящую Политику. Актуальная редакция размещается на данной
      странице. Продолжение использования Сайта после изменения Политики означает согласие
      пользователя с новой редакцией.
    </p>

    <div className="requisites">
      <strong>Оператор:</strong>
      <p>
        ИП Пушкарева Мария Викторовна
        <br />
        E-mail: {email}
      </p>
    </div>
  </LegalPage>
);

PrivacyPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default PrivacyPage;
